import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { WarningAmber } from 'components/icons';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Separator } from 'components/ui/separator';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { InvoiceSendValidationDto } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

export interface InvoiceValidationItem {
    invoiceId: number;
    invoiceNumber: string;
    validationData: InvoiceSendValidationDto;
}

interface Props {
    onConfirm: () => void;
    onCancel: () => void;
    invoices: InvoiceValidationItem[];
}

interface ValidationAlertsProps {
    validationData: InvoiceSendValidationDto;
    today: string;
    formatDate: (v: string | null | undefined) => string;
}

const ValidationAlerts: React.FC<ValidationAlertsProps> = ({ validationData, today, formatDate }) => (
    <>
        {validationData.hasInvoiceDateWarning && (
            <Alert className="border-warning bg-warning-light new:border-transparent new:bg-warning-tint">
                <WarningAmber className="text-warning size-4" />
                <AlertDescription className="text-foreground w-full">
                    <p className="text-body2 new:text-sm">
                        <FormattedMessage
                            id="invoices.send-validation.invoice-date-message"
                            values={{
                                proposedInvoiceDate: formatDate(validationData.proposedInvoiceDate),
                            }}
                        />
                    </p>
                </AlertDescription>
            </Alert>
        )}
        {validationData.hasDueDateWarning && (
            <Alert className="border-warning bg-warning-light new:border-transparent new:bg-warning-tint">
                <WarningAmber className="text-warning size-4" />
                <AlertDescription className="text-foreground w-full">
                    <p className="text-body2 new:text-sm">
                        <FormattedMessage
                            id="invoices.send-validation.due-date-message"
                            values={{
                                paymentTermInDays: validationData.paymentDeadlineInDays,
                                today: today,
                                proposedDueDate: formatDate(validationData.proposedDueDate),
                            }}
                        />
                    </p>
                </AlertDescription>
            </Alert>
        )}
        {validationData.hasUnusedPrepayment && (
            <Alert className="border-warning bg-warning-light new:border-transparent new:bg-warning-tint">
                <WarningAmber className="text-warning size-4" />
                <AlertDescription className="text-foreground w-full">
                    <p className="text-body2 new:text-sm">
                        <FormattedMessage id="invoices.send-validation.prepayment-message" />
                    </p>
                </AlertDescription>
            </Alert>
        )}
        {validationData.hasLinkedPrepaymentPaidAfterInvoiceDate && (
            <Alert className="border-warning bg-warning-light new:border-transparent new:bg-warning-tint">
                <WarningAmber className="text-warning size-4" />
                <AlertDescription className="text-foreground w-full">
                    <p className="text-body2 new:text-sm">
                        <FormattedMessage id="invoices.send-validation.prepayment-paid-date-message" />
                    </p>
                </AlertDescription>
            </Alert>
        )}
    </>
);

const InvoiceSendValidationModal: React.FC<Props> = ({ onConfirm, onCancel, invoices }) => {
    const isNew = useIsNewDesign();
    const effectiveFormat = useEffectiveDateFormat();
    const formatDate = (v: string | null | undefined) => (v ? format(new Date(v), effectiveFormat) : '');
    const today = format(new Date(), effectiveFormat);

    const isSingleInvoice = invoices.length === 1;
    const title = t('invoices.send-validation.title');

    return (
        <>
            <ModalHeader
                title={title}
                description={
                    isSingleInvoice ? t('invoices.send-invoice', { number: invoices[0].invoiceNumber }) : undefined
                }
                onCancel={onCancel}
            />
            <div
                className={cn(
                    'p-6 max-sm:px-4',
                    !isSingleInvoice && 'max-h-[60dvh] overflow-y-auto max-sm:max-h-[50dvh]',
                    // A side sheet in the new design: the body takes the height and scrolls, the footer stays at its foot.
                    'new:max-h-none new:flex-1 new:overflow-y-auto'
                )}
            >
                <div className="flex flex-col gap-6">
                    {invoices.map((invoice, index) => (
                        <div key={invoice.invoiceId}>
                            <h6 className="text-h6 m-0 mb-4 font-semibold">
                                {isNew
                                    ? t('invoices.send-validation.invoice-heading', { number: invoice.invoiceNumber })
                                    : `${t('invoices.invoice-number')}: ${invoice.invoiceNumber}`}
                            </h6>
                            <div className="flex flex-col gap-4">
                                <ValidationAlerts
                                    validationData={invoice.validationData}
                                    today={today}
                                    formatDate={formatDate}
                                />
                            </div>
                            {!isSingleInvoice && index < invoices.length - 1 && <Separator className="mt-6" />}
                        </div>
                    ))}
                </div>
            </div>
            <ModalFooter
                onCancel={onCancel}
                onSave={onConfirm}
                saveButtonTextId="invoices.send-validation.proceed"
                cancelButtonTextId="invoices.send-validation.dont-proceed"
            />
        </>
    );
};

export default InvoiceSendValidationModal;
